import React, {FC} from "react";
import {Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography} from "@mui/material";
import ThicknessClassName from "../../thicknessClass/ThicknessClassDetail/ThicknessClassName";
import TimberClassName from "../../timberClass/TimberClassDetail/TimberClassName";
import TimberTypeName from "../../timberType/TimberTypeDetail/TimberTypeName";
import {Shipment} from "../../../types/models/Shipment";
import {PrimaryLog} from "../../../../timber/types/models/Log";
import {styled} from "@mui/material/styles";
import { Trans } from "@lingui/react/macro";
import {useGetLogHistoryByContextQuery} from "../../../apis/LogHistory.api.ts";
import GradientCircularProgress from "../../../../share/components/progress/GradientCircularProgress";
import {fixedFormat} from "../../../../utils/numberFormat";

const SmallTableHeaderRow = styled(TableRow)(({theme}) => ({
    '&.MuiTableRow-root': {
        [theme.breakpoints.up('md')]: {
            display: 'none',
        },
    },
}));

const SmallTableRow = styled(TableRow)(({theme}) => ({
    '&.MuiTableRow-root': {
        cursor: "pointer",
        [theme.breakpoints.up('md')]: {
            display: 'none',
        },
    },
}));

const BigTableHeaderRow = styled(TableRow)(({theme}) => ({
    '&.MuiTableRow-root': {
        [theme.breakpoints.down('md')]: {
            display: 'none',
        },
    },
}));

const BigTableRow = styled(TableRow)(({theme}) => ({
    '&.MuiTableRow-root': {
        cursor: "pointer",
        [theme.breakpoints.down('md')]: {
            display: 'none',
        },
    },
}));

type IFormSelectProps = {
    shipment: Shipment;
}

const LogListItemFromHistory: FC<IFormSelectProps> = ({shipment}) => {

    const {page, pageSize} = {page: 0, pageSize: 100000}

    const { data: list = {
        items: [],
        totalItems: 0,
        page: 0,
        pageSize: 12,
        numPages: 0
    },
        isLoading: listLoading,
        isFetching: listFetching,
    } = useGetLogHistoryByContextQuery({
        list: {
            page,
            pageSize,
        },
        filter: {
            cuttingId: shipment.cuttingId,
            action: 'shipment/cancel',
            contextHistory: shipment.id,
        }
    })

    if (listLoading || listFetching) {
        return <GradientCircularProgress/>
    }

    return (
    <Box>
        <Paper>
            <Paper>
                <TableContainer>
                    <Table stickyHeader aria-label="simple table" size="small">
                        <TableHead>
                            <SmallTableHeaderRow>
                                <TableCell component="th" align="center"><Trans>No</Trans></TableCell>
                                <TableCell component="th" align="left"><Trans>Timber Key</Trans></TableCell>
                                <TableCell component="th" align="right"><Trans>Volume (m&#xB3;)</Trans></TableCell>
                                <TableCell component="th" align="left"><Trans>Timber Type</Trans></TableCell>
                            </SmallTableHeaderRow>
                            <BigTableHeaderRow>
                                <TableCell component="th" align="center"><Trans>No</Trans></TableCell>
                                <TableCell component="th" align="center"><Trans>Timber Key</Trans></TableCell>
                                <TableCell component="th" align="center"><Trans>Diameter</Trans></TableCell>
                                <TableCell component="th" align="center"><Trans>Length</Trans></TableCell>
                                <TableCell component="th" align="center"><Trans>Volume</Trans></TableCell>
                                <TableCell component="th" align="center"><Trans>Timber Type</Trans></TableCell>
                                <TableCell component="th" align="center"><Trans>Class</Trans></TableCell>
                                <TableCell component="th" align="center"><Trans>Thickness Class</Trans></TableCell>
                            </BigTableHeaderRow>
                        </TableHead>
                        <TableBody>
                            { list.items.map((log, index) => (
                                <React.Fragment key={ PrimaryLog.fromEntity(log).toPath() }>
                                    <SmallTableRow
                                        hover={true}
                                        key={ PrimaryLog.fromEntity(log).toPath() + '-small' }
                                    >
                                        <TableCell scope="row" align="center">
                                            { (page * pageSize) + index + 1 }.
                                        </TableCell>
                                        <TableCell scope="row">
                                            <Typography component="div" sx={ { fontWeight: 'bold', } }>
                                                { log.timberKey ?
                                                    <>
                                                        { log.timberKey }
                                                    </>
                                                    :
                                                    '-'
                                                }
                                            </Typography>


                                            <Typography component="div" variant="body1">
                                                { log.externalKey &&
                                                    <>
                                                        <Trans>External Key</Trans>:&nbsp;
                                                        { log.externalKey }
                                                    </>
                                                }
                                            </Typography>


                                            <Typography component="div" variant="body1">
                                                { log.internalKey &&
                                                    <>
                                                        <Trans>IK</Trans>:&nbsp;
                                                        { log.internalKey }
                                                    </>
                                                }
                                            </Typography>
                                        </TableCell>
                                        <TableCell scope="row" align="right">
                                            <Typography component="div" sx={ { fontWeight: 'bold', } }>
                                                { log.volume ?
                                                    <>
                                                        {fixedFormat(log.volume) }
                                                    </>
                                                    :
                                                    '-'
                                                }
                                            </Typography>

                                            <Typography component="div" variant="body1">
                                                { log.diameter &&
                                                    <>
                                                        {(log.diameter) }
                                                    </>
                                                }
                                            </Typography>


                                            <Typography component="div" variant="body1">
                                                { log.length &&
                                                    <>
                                                        {(log.length) }
                                                    </>
                                                }
                                            </Typography>

                                        </TableCell>
                                        <TableCell scope="row" align="left">
                                            <Typography component="div" sx={ { fontWeight: 'bold', } }>
                                                { log.timberTypeId ?
                                                    <>
                                                        <TimberTypeName id={ log.timberTypeId }/>
                                                    </>
                                                    :
                                                    '-'
                                                }
                                            </Typography>


                                            <Typography component="div" variant="body1">
                                                { log.classId &&
                                                    <>
                                                        <Trans>C</Trans>:&nbsp;
                                                        <TimberClassName id={ log.classId }/>
                                                    </>
                                                }
                                            </Typography>


                                            <Typography component="div" variant="body1">
                                                { log.thicknessClassId &&
                                                    <>
                                                        <Trans>TC</Trans>:&nbsp;
                                                        <ThicknessClassName id={ log.thicknessClassId }/>
                                                    </>
                                                }
                                            </Typography>
                                        </TableCell>
                                    </SmallTableRow>
                                    <BigTableRow
                                        hover={true}
                                        key={ PrimaryLog.fromEntity(log).toPath() + '-big' }
                                    >
                                        <TableCell width="20%" scope="row" align="center">
                                            { (page * pageSize) + index + 1 }.
                                        </TableCell>
                                        <TableCell width="20%" scope="row" align="center">
                                            { log.internalKey }
                                        </TableCell>
                                        <TableCell width="20%" scope="row" align="right">
                                            { log.diameter }
                                        </TableCell>
                                        <TableCell width="20%" scope="row" align="right">
                                            { log.length }
                                        </TableCell>
                                        <TableCell width="20%" scope="row" align="right">
                                            {fixedFormat(log.volume)}
                                        </TableCell>
                                        <TableCell width="20%" scope="row" align="center">
                                            <TimberTypeName id={log.timberTypeId}/>
                                        </TableCell>
                                        <TableCell width="20%" scope="row" align="center">
                                            <TimberClassName id={log.classId}/>
                                        </TableCell>
                                        <TableCell width="20%" scope="row" align="center">
                                            <ThicknessClassName id={log.thicknessClassId}/>
                                        </TableCell>
                                    </BigTableRow>
                                </React.Fragment>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </Paper>
    </Box>
    )
}

export default LogListItemFromHistory;
